import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import emailjs from '@emailjs/browser';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // EmailJS Configuration
      // IMPORTANT: Set up your EmailJS account at https://www.emailjs.com/
      // 1. Create an account
      // 2. Add an email service (Gmail recommended)
      // 3. Create an email template
      // 4. Replace the values below with your actual IDs
      
      const serviceId = 'service_8p0gm4v'; // Replace with your EmailJS service ID
      const templateId = 'template_6ndcv1o'; // Replace with your EmailJS template ID
      const publicKey = 'Fqz_xEx8d4-8QCRE0'; // Replace with your EmailJS public key
      
      // Template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        to_email: 'hello@nexendigital.com'
      };
      
      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send message. Please try again or email us directly at hello@nexendigital.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dark-container">
      <SEO 
        title="Contact Us - Website Development & App Development Company in Bhubaneswar | NEXEN DIGITAL"
        description="Contact NEXEN DIGITAL for professional website development and app development services in Bhubaneswar. Get in touch for custom web design, mobile app development, and software solutions in Odisha. Call +91-9556898310 or email hello@nexendigital.com"
        keywords="contact website development bhubaneswar, contact app development bhubaneswar, web development inquiry, get quote website development, hire web developers bhubaneswar, bhubaneswar, odisha"
      />
      {/* Hero Section */}
      <section style={{ padding: '100px 0 60px', textAlign: 'center' }}>
        <h1 className="display-huge" style={{ marginBottom: '24px', color: '#FFFFFF' }}>
          Get in <span style={{ color: '#00FFD1' }}>Touch</span>
        </h1>
        <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto', color: 'rgba(255, 255, 255, 0.85)' }}>
          Have a project in mind or questions about our services? We'd love to hear from you. Let's start a conversation.
        </p>
      </section>

      {/* Contact Section */}
      <section style={{ padding: '60px 0 100px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '60px' }}>
          {/* Contact Info */}
          <div>
            <h2 className="display-medium" style={{ marginBottom: '40px', color: '#FFFFFF' }}>
              Contact <span style={{ color: '#00FFD1' }}>Information</span>
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                <div style={{ background: 'rgba(0, 255, 209, 0.1)', padding: '16px', border: '1px solid rgba(0, 255, 209, 0.3)' }}>
                  <MapPin size={32} style={{ color: '#00FFD1' }} />
                </div>
                <div>
                  <h3 className="heading-2" style={{ marginBottom: '8px', color: '#FFFFFF' }}>Location</h3>
                  <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {companyInfo.location}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                <div style={{ background: 'rgba(0, 255, 209, 0.1)', padding: '16px', border: '1px solid rgba(0, 255, 209, 0.3)' }}>
                  <Mail size={32} style={{ color: '#00FFD1' }} />
                </div>
                <div>
                  <h3 className="heading-2" style={{ marginBottom: '8px', color: '#FFFFFF' }}>Email</h3>
                  <a href={`mailto:${companyInfo.email}`} className="body-large" style={{ color: 'rgba(255, 255, 255, 0.85)', textDecoration: 'none' }}>
                    {companyInfo.email}
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                <div style={{ background: 'rgba(0, 255, 209, 0.1)', padding: '16px', border: '1px solid rgba(0, 255, 209, 0.3)' }}>
                  <Phone size={32} style={{ color: '#00FFD1' }} />
                </div>
                <div>
                  <h3 className="heading-2" style={{ marginBottom: '8px', color: '#FFFFFF' }}>Phone</h3>
                  <a href={`tel:${companyInfo.phone}`} className="body-large" style={{ color: 'rgba(255, 255, 255, 0.85)', textDecoration: 'none' }}>
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div style={{ background: '#121212', padding: '60px', border: '1px solid rgba(255, 255, 255, 0.25)' }}>
            <h2 className="heading-1" style={{ marginBottom: '32px', color: '#FFFFFF' }}>Send us a Message</h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <Label htmlFor="name" style={{ color: '#FFFFFF', marginBottom: '8px', display: 'block' }}>Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{ background: '#000000', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#FFFFFF', padding: '14px' }}
                />
              </div>
              <div>
                <Label htmlFor="email" style={{ color: '#FFFFFF', marginBottom: '8px', display: 'block' }}>Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{ background: '#000000', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#FFFFFF', padding: '14px' }}
                />
              </div>
              <div>
                <Label htmlFor="phone" style={{ color: '#FFFFFF', marginBottom: '8px', display: 'block' }}>Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{ background: '#000000', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#FFFFFF', padding: '14px' }}
                />
              </div>
              <div>
                <Label htmlFor="subject" style={{ color: '#FFFFFF', marginBottom: '8px', display: 'block' }}>Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  style={{ background: '#000000', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#FFFFFF', padding: '14px' }}
                />
              </div>
              <div>
                <Label htmlFor="message" style={{ color: '#FFFFFF', marginBottom: '8px', display: 'block' }}>Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  required
                  style={{ background: '#000000', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#FFFFFF', padding: '14px' }}
                />
              </div>
              <button 
                type="submit" 
                className="btn-primary" 
                style={{ width: '100%', justifyContent: 'center', opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};